import type React from "react";

import { CornerDownLeft, DotIcon, PaperclipIcon, SmileIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/auth";

export interface Message {
	id: string;
	text: string;
	sender: "user" | "agent";
	timestamp: Date;
	status?: "sending" | "sent" | "error";
}

export const initialMessages: Message[] = [
	{
		id: crypto.randomUUID(),
		text: "Hello! Welcome to Intasida support. How may I assist you today?",
		sender: "agent",
		timestamp: new Date(Date.now() - 24 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Hi, I'm having trouble with my account settings",
		sender: "user",
		timestamp: new Date(Date.now() - 23 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "I can help you with that. What specific settings are you trying to modify?",
		sender: "agent",
		timestamp: new Date(Date.now() - 22 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "I can't change my profile picture",
		sender: "user",
		timestamp: new Date(Date.now() - 21 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Let me guide you through the process. What format is your image file?",
		sender: "agent",
		timestamp: new Date(Date.now() - 20 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "It's a PNG file",
		sender: "user",
		timestamp: new Date(Date.now() - 19 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Perfect. Please ensure the file size is under 5MB. Are you getting any error messages?",
		sender: "agent",
		timestamp: new Date(Date.now() - 18 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Yes, it says 'Upload failed'",
		sender: "user",
		timestamp: new Date(Date.now() - 17 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Could you try clearing your browser cache and attempting again?",
		sender: "agent",
		timestamp: new Date(Date.now() - 16 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Ok, I'll try that now",
		sender: "user",
		timestamp: new Date(Date.now() - 15 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Take your time. Let me know if it works.",
		sender: "agent",
		timestamp: new Date(Date.now() - 14 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "It worked! Thank you!",
		sender: "user",
		timestamp: new Date(Date.now() - 13 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Excellent! Is there anything else you need help with?",
		sender: "agent",
		timestamp: new Date(Date.now() - 12 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Yes, one more thing. How do I change my email preferences?",
		sender: "user",
		timestamp: new Date(Date.now() - 11 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Go to Settings > Notifications > Email Preferences. You'll find all options there.",
		sender: "agent",
		timestamp: new Date(Date.now() - 10 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Got it, I see the options now",
		sender: "user",
		timestamp: new Date(Date.now() - 9 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Perfect! Remember to save changes after selecting your preferences.",
		sender: "agent",
		timestamp: new Date(Date.now() - 8 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Done! Everything's working great now",
		sender: "user",
		timestamp: new Date(Date.now() - 7 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Wonderful! Is there anything else you'd like to know?",
		sender: "agent",
		timestamp: new Date(Date.now() - 6 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "No, that's all. Thanks for your help!",
		sender: "user",
		timestamp: new Date(Date.now() - 5 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "You're welcome! Feel free to reach out if you need anything else.",
		sender: "agent",
		timestamp: new Date(Date.now() - 4 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Will do, goodbye!",
		sender: "user",
		timestamp: new Date(Date.now() - 3 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Goodbye! Have a great day!",
		sender: "agent",
		timestamp: new Date(Date.now() - 2 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "Thanks, you too!",
		sender: "user",
		timestamp: new Date(Date.now() - 1 * 300000),
		status: "sent"
	},
	{
		id: crypto.randomUUID(),
		text: "🙂👋",
		sender: "agent",
		timestamp: new Date(),
		status: "sent"
	}
];

const CHAT_URL = `${import.meta.env.VITE_BASE_URL?.replace("/v1", "")}/chat`;

export default function ChatInterface({ className }: { className?: string }) {
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [textareaHeight, setTextareaHeight] = useState(56);
	const accessToken = useAuthStore(state => state.tokens?.accessToken);

	useEffect(() => {
		if (!accessToken)
			return;

		const socket = io(CHAT_URL, {
			autoConnect: true,
			forceNew: false,
			reconnection: true,
			reconnectionAttempts: 8,
			reconnectionDelay: 2000,
			reconnectionDelayMax: 30_000,
			timeout: 15_000,
			transports: ["websocket", "polling"],
			upgrade: true,
			auth: {
				token: accessToken,
				authorization: `Bearer ${accessToken}`
			}
		});

		const onConnect = () => {
			socket.emit("JOIN_CHAT", { chatId: crypto.randomUUID() });
		};

		socket.on("connect", onConnect);
		socket.on("USER_JOINED_CHAT", () => undefined);
		socket.on("RECEIVE_MESSAGE", (newMessage: Message) => {
			setMessages(prevMessages => [...prevMessages, newMessage]);
		});

		return () => {
			socket.removeAllListeners();
			socket.disconnect();
		};
	}, [accessToken]);

	const formatTimestamp = (date: Date) => {
		return new Intl.DateTimeFormat("en-US", {
			hour: "numeric",
			minute: "numeric",
			hour12: true
		}).format(date);
	};

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const adjustTextareaHeight = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "56px";
			const scrollHeight = textareaRef.current.scrollHeight;
			const newHeight = Math.min(scrollHeight, 200);
			setTextareaHeight(newHeight);
			textareaRef.current.style.height = `${newHeight}px`;
		}
	};

	const handleSendMessage = (e: React.FormEvent) => {
		e.preventDefault();
		if (input.trim()) {
			const newUserMessage: Message = {
				id: crypto.randomUUID(),
				text: input,
				sender: "user",
				timestamp: new Date(),
				status: "sending"
			};

			setMessages(prev => [...prev, newUserMessage]);
			setInput("");
			adjustTextareaHeight();

			setTimeout(() => {
				setMessages(prevMessages =>
					prevMessages.map(msg => (msg.id === newUserMessage.id ? { ...msg, status: "sent" } : msg))
				);

				setIsTyping(true);

				setTimeout(() => {
					const agentResponse: Message = {
						id: crypto.randomUUID(),
						text: "Thanks for your message. An agent will respond shortly.",
						sender: "agent",
						timestamp: new Date()
					};

					setMessages(prev => [...prev, agentResponse]);
					setIsTyping(false);
				}, 1000);
			}, 500);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage(e);
		}
	};

	useEffect(() => {
		adjustTextareaHeight();
	}, [input]);

	return (
		<div className={cn("p-0 flex flex-col h-[90vh]", className)}>
			<div className="p-4 bg-[#2A2A2A] text-secondary rounded-t-lg">
				<p className="mb-2 font-medium">Hey there!</p>
				<p className="text-xs font-thin opacity-80">
					Welcome to Intasida. Please let us know how we can help you today.
				</p>
			</div>

			<div className="flex-grow overflow-y-auto p-4" style={{ height: `calc(100% - ${textareaHeight + 32}px)` }}>
				<div
					className={cn("mb-4 text-center text-xs text-gray-600", {
						hidden: messages.length
					})}
				>
					START A CONVERSATION
				</div>
				{messages.map(message => (
					<div key={message.id} className={`flex mb-4 ${message.sender === "user" ? "justify-end" : "justify-start"}`}>
						<div
							className={`relative max-w-[75%] rounded-lg text-sm p-2 border border-border ${
								message.sender === "user"
									? "bg-hover-secondary text-secondary-foreground"
									: "bg-secondary  text-secondary-foreground "
							}`}
						>
							{message.text}
							<div className="mt-1 text-[10px] opacity-70 text-right">{formatTimestamp(message.timestamp)}</div>
							{message.status === "sending" && (
								<span className="absolute -bottom-3 right-0 text-xs text-gray-500">Sending...</span>
							)}
							{message.status === "error" && (
								<span className="absolute -bottom-3 right-0 text-xs text-red-500">Failed to send</span>
							)}
						</div>
					</div>
				))}
				{isTyping && (
					<div className="flex justify-start mb-4">
						<DotIcon className="h-4 w-4 text-secondary-foreground animate-pulse" />
						<DotIcon className="h-4 w-4 text-secondary-foreground animate-pulse" />
						<DotIcon className="h-4 w-4 text-secondary-foreground animate-pulse" />
					</div>
				)}
				<div ref={messagesEndRef} />
			</div>

			<div className="p-4 border-t mt-auto">
				<form onSubmit={handleSendMessage} className="relative flex items-end">
					<Textarea
						ref={textareaRef}
						value={input}
						onChange={e => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Send a message..."
						className="pr-24 resize-none overflow-y-auto placeholder:text-sm text-sm"
						style={{ height: `${textareaHeight}px`, maxHeight: "200px" }}
					/>
					<div className="absolute right-0 bottom-0 flex items-center p-2">
						<Button type="button" variant="ghost" size="icon" className="h-8 w-8">
							<SmileIcon className="h-5 w-5 text-muted-foreground" />
						</Button>
						<Button type="button" variant="ghost" size="icon" className="h-8 w-8">
							<PaperclipIcon className="h-5 w-5 text-muted-foreground" />
						</Button>
						<Button type="submit" size="icon" className="h-8 w-8" disabled={!input.trim()}>
							<CornerDownLeft className="h-4 w-4" />
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
